import { z } from "zod";

/**
 * Client-initiated top-up — 0115. Server floors this at the QR Ph technical
 * minimum (₱1.00, same as any online payment); no business minimum is
 * defined anywhere in the vault (OQ-36 lists "minimum top-up" as an open
 * question), so none is enforced here beyond what PayMongo itself requires.
 */
export const createTopupSchema = z.object({
  amountPhp: z.number().positive().max(1_000_000),
});

export type CreateTopupInput = z.infer<typeof createTopupSchema>;

/** A top-up paid with Maya (0360): the amount, and where Maya sends the client back. */
export const createTopupMayaSchema = z.object({
  amountPhp: z.number().positive().max(1_000_000),
  returnUrl: z.string().url().max(500),
});

export type CreateTopupMayaInput = z.infer<typeof createTopupMayaSchema>;

/**
 * Superadmin-only manual credit adjustment (0115's `adjust_client_credits`)
 * — the build-the-capability half of never-invent-policy for reversal/
 * chargeback handling and ordinary support cases. Never client-reachable.
 */
export const adjustCreditsSchema = z.object({
  clientId: z.string().uuid(),
  amountPhp: z.number().positive().max(1_000_000),
  type: z.enum(["system_refund", "reversal"]),
  note: z.string().trim().min(3).max(500),
});

export type AdjustCreditsInput = z.infer<typeof adjustCreditsSchema>;

/**
 * Credits as a selectable online checkout channel (0116) — full-cover-only,
 * client-chosen, never auto-applied. The booking is the only client-supplied
 * value; amount, balance, and outcome are all server truth.
 */
export const settleBookingWithCreditsSchema = z.object({
  bookingId: z.string().uuid(),
});

export type SettleBookingWithCreditsInput = z.infer<typeof settleBookingWithCreditsSchema>;

/**
 * What a cancellation returned to the client — CORE-08 §7: the client "sees …
 * applicable fee, Credits returned, Gift Pass restoration". Returned by both
 * cancel routes (ASAP `PATCH /bookings/:id/status`, Scheduled
 * `POST /bookings/scheduled/cancel`) as `cancellation`, and null when the
 * figures are not available yet (issuance is best-effort and caught up on
 * retry — never presented as "you got nothing"). 0267 → 0269.
 */
export const cancellationSummarySchema = z.object({
  feePhp: z.number().nonnegative(),
  creditsReturnedPhp: z.number().nonnegative(),
  giftpassesRestored: z.number().int().nonnegative(),
});

export type CancellationSummary = z.infer<typeof cancellationSummarySchema>;
